import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { Loader2, Upload, X, Send } from "lucide-react";
import { toast } from "sonner";
import { ENDPOINTS, SITE } from "@/config/site";
import { PROJECT_TYPES, CLIENT_TYPES, PROPERTY_TYPES, DELAYS, BUDGETS, SOURCES } from "@/data/services";

const schema = z.object({
  clientType: z.string().min(1, "Merci d'indiquer qui vous êtes."),
  projectType: z.string().min(1, "Merci de choisir un type de projet."),
  company: z.string().max(120).optional(),
  name: z.string().min(2, "Merci d'indiquer vos nom et prénom."),
  email: z.string().email("Adresse email invalide."),
  phone: z.string().regex(/^(\+33|0)[1-9](?:[\s.-]?\d{2}){4}$/, "Numéro de téléphone invalide (ex. 06 12 34 56 78)."),
  street: z.string().min(3, "Merci d'indiquer la rue."),
  postalCode: z.string().regex(/^\d{5}$/, "Code postal à 5 chiffres."),
  city: z.string().min(2, "Merci d'indiquer la ville."),
  propertyType: z.string().min(1, "Merci de choisir un type de bien."),
  surface: z.string().regex(/^\d*$/, "Surface en chiffres uniquement.").optional(),
  description: z.string().min(30, "Décrivez votre projet en 30 caractères minimum."),
  delay: z.string().optional(),
  budget: z.string().optional(),
  source: z.string().optional(),
  rgpd: z.literal(true, { errorMap: () => ({ message: "Votre consentement est nécessaire pour traiter la demande." }) }),
  marketing: z.boolean().optional(),
  website: z.string().max(0).optional(),
});

const Field = ({ label, name, error, required, children, hint }) => (
  <div>
    <label htmlFor={name} className="label">{label}{required && <span className="text-copper"> *</span>}</label>
    {children}
    {hint && !error && <p className="mt-1.5 text-xs text-muted">{hint}</p>}
    {error && <p className="error-text" role="alert" data-testid={`error-${name}`}>{error.message}</p>}
  </div>
);

const Select = ({ name, register, options, error, placeholder = "Sélectionner…" }) => (
  <select id={name} {...register(name)} className={`field ${error ? "field-error" : ""}`} data-testid={`devis-${name}`}>
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export const DevisForm = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const ts = useMemo(() => Date.now(), []);
  const { register, handleSubmit, setValue, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { projectType: params.get("prestation") || "", marketing: false, website: "" },
  });

  useEffect(() => {
    const p = params.get("prestation");
    if (p && PROJECT_TYPES.includes(p)) setValue("projectType", p);
  }, [params, setValue]);

  const onFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    const all = [...files, ...incoming];
    setFileError("");
    if (all.length > 5) return setFileError("Maximum 5 fichiers.");
    for (const f of incoming) {
      if (!/\.(jpe?g|png|pdf)$/i.test(f.name)) return setFileError("Formats acceptés : jpg, png, pdf.");
      if (f.size > 5 * 1024 * 1024) return setFileError(`« ${f.name} » dépasse 5 Mo.`);
    }
    setFiles(all);
    e.target.value = "";
  };

  const onSubmit = async (data) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => fd.append(k, v === undefined ? "" : String(v)));
    fd.append("ts", String(ts));
    files.forEach((f) => fd.append("files", f));
    try {
      const res = await fetch(ENDPOINTS.devis, { method: "POST", body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.detail || json.error || "Erreur d'envoi");
      toast.success("Votre demande a bien été envoyée. Nous vous répondons sous 48 heures ouvrées.");
      navigate("/merci", { state: { type: "devis", name: data.name } });
    } catch (err) {
      toast.error(`L'envoi a échoué (${err.message}). Appelez-nous au ${SITE.phone.display} ou écrivez à ${SITE.email}.`);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6" data-testid="devis-form">
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" {...register("website")} data-testid="devis-honeypot" />

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Vous êtes" name="clientType" error={errors.clientType} required><Select name="clientType" register={register} options={CLIENT_TYPES} error={errors.clientType} /></Field>
        <Field label="Type de projet" name="projectType" error={errors.projectType} required><Select name="projectType" register={register} options={PROJECT_TYPES} error={errors.projectType} /></Field>
        <Field label="Société" name="company" error={errors.company} hint="Facultatif si vous êtes un particulier."><input id="company" {...register("company")} className="field" data-testid="devis-company" /></Field>
        <Field label="Nom et prénom" name="name" error={errors.name} required><input id="name" {...register("name")} autoComplete="name" className={`field ${errors.name ? "field-error" : ""}`} data-testid="devis-name" /></Field>
        <Field label="Email" name="email" error={errors.email} required><input id="email" type="email" {...register("email")} autoComplete="email" className={`field ${errors.email ? "field-error" : ""}`} data-testid="devis-email" /></Field>
        <Field label="Téléphone" name="phone" error={errors.phone} required><input id="phone" type="tel" {...register("phone")} autoComplete="tel" className={`field ${errors.phone ? "field-error" : ""}`} data-testid="devis-phone" /></Field>
      </div>

      <fieldset className="grid sm:grid-cols-6 gap-5">
        <legend className="label mb-3">Adresse du chantier <span className="text-copper">*</span></legend>
        <div className="sm:col-span-3"><Field label="Rue" name="street" error={errors.street} required><input id="street" {...register("street")} className={`field ${errors.street ? "field-error" : ""}`} data-testid="devis-street" /></Field></div>
        <div className="sm:col-span-1"><Field label="Code postal" name="postalCode" error={errors.postalCode} required><input id="postalCode" inputMode="numeric" {...register("postalCode")} className={`field ${errors.postalCode ? "field-error" : ""}`} data-testid="devis-postal-code" /></Field></div>
        <div className="sm:col-span-2"><Field label="Ville" name="city" error={errors.city} required><input id="city" {...register("city")} className={`field ${errors.city ? "field-error" : ""}`} data-testid="devis-city" /></Field></div>
      </fieldset>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Type de bien" name="propertyType" error={errors.propertyType} required><Select name="propertyType" register={register} options={PROPERTY_TYPES} error={errors.propertyType} /></Field>
        <Field label="Surface approximative (m²)" name="surface" error={errors.surface}><input id="surface" inputMode="numeric" {...register("surface")} className={`field ${errors.surface ? "field-error" : ""}`} data-testid="devis-surface" /></Field>
      </div>

      <Field label="Description détaillée du projet" name="description" error={errors.description} required hint="30 caractères minimum. Nature des travaux, état actuel, contraintes d'accès, etc.">
        <textarea id="description" rows={5} {...register("description")} className={`field ${errors.description ? "field-error" : ""}`} data-testid="devis-description" />
      </Field>

      <div className="grid sm:grid-cols-3 gap-5">
        <Field label="Délai souhaité" name="delay" error={errors.delay}><Select name="delay" register={register} options={DELAYS} /></Field>
        <Field label="Budget indicatif" name="budget" error={errors.budget}><Select name="budget" register={register} options={BUDGETS} /></Field>
        <Field label="Comment nous avez-vous connu ?" name="source" error={errors.source}><Select name="source" register={register} options={SOURCES} /></Field>
      </div>

      <div>
        <p className="label">Photos du projet <span className="text-muted font-normal">(facultatif — jpg, png, pdf · 5 Mo max · 5 fichiers max)</span></p>
        <label htmlFor="files" className="flex items-center justify-center gap-3 border-2 border-dashed border-line rounded-xl px-4 py-6 cursor-pointer hover:border-copper hover:bg-copper-soft/40 transition-colors text-sm font-semibold text-slate" data-testid="devis-files-dropzone">
          <Upload size={18} className="text-copper" /> Ajouter des fichiers
          <input id="files" type="file" multiple accept=".jpg,.jpeg,.png,.pdf" onChange={onFiles} className="sr-only" data-testid="devis-files-input" />
        </label>
        {fileError && <p className="error-text" role="alert" data-testid="error-files">{fileError}</p>}
        {files.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-2" data-testid="devis-files-list">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-2 bg-concrete rounded-full pl-3 pr-1 py-1 text-xs font-medium text-slate">
                {f.name} <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="p-1 rounded-full hover:bg-white" aria-label={`Retirer ${f.name}`}><X size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-3">
        <label className="flex items-start gap-3 text-sm text-ink/85">
          <input type="checkbox" {...register("rgpd")} className="mt-1 w-4 h-4 accent-copper" data-testid="devis-rgpd" />
          <span>J'accepte que mes données soient traitées par O'RIZON 360 dans le cadre de ma demande de devis, conformément à la <Link to="/politique-de-confidentialite" className="text-copper underline underline-offset-2">politique de confidentialité</Link>. <span className="text-copper">*</span></span>
        </label>
        {errors.rgpd && <p className="error-text" role="alert" data-testid="error-rgpd">{errors.rgpd.message}</p>}
        <label className="flex items-start gap-3 text-sm text-ink/85">
          <input type="checkbox" {...register("marketing")} className="mt-1 w-4 h-4 accent-copper" data-testid="devis-marketing" />
          <span>Je souhaite recevoir des informations commerciales de O'RIZON 360.</span>
        </label>
      </div>

      <button type="submit" disabled={isSubmitting} className="btn-primary w-full sm:w-auto disabled:opacity-60" data-testid="devis-submit-button">
        {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} Envoyer ma demande de devis
      </button>
      <p className="text-xs text-muted">Les informations transmises sont confidentielles et ne sont en aucun cas cédées à des tiers.</p>
    </form>
  );
};
